import React, { useState } from 'react';
import {
  DocumentTextIcon,
  ArrowPathIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
  SparklesIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  BuildingOffice2Icon,
  BoltIcon,
  CalendarDaysIcon,
  PlayIcon,
  PauseIcon,
  ChartBarIcon,
  ServerIcon,
  XCircleIcon,
  PlusIcon,
  DocumentArrowUpIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import { useUTILMD } from '../hooks/useUTILMD';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { storage, db } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { UTILMDGenerator } from '../utils/utilmdGenerator';
import { UTILMDFormat, UTILMDMarketSettings } from '../types/utilmd';

interface UTILMDFeature {
  id: string;
  title: string;
  description: string;
  icon: React.FC<React.ComponentProps<'svg'>>;
  status: 'planned' | 'development' | 'testing';
  estimatedDate: string;
}

interface PODChangeRequest {
  podId: string;
  customerName: string;
  customerAddress: string;
  contractStartDate: string;
  contractEndDate: string;
  messageType: 'C01' | 'C03' | 'E01';
  provider: 'EON' | 'ELMU' | 'DEMASZ' | 'OPUS' | 'MVMI';
  newTrader: string;
  oldTrader?: string;
  attachments?: string[];
}

interface CustomerTransferRequest {
  podId: string;
  currentCustomerName: string;
  currentCustomerAddress: string;
  newCustomerName: string;
  newCustomerAddress: string;
  newCustomerPhone: string;
  newCustomerEmail: string;
  transferDate: string;
  transferReason: 'ownership_change' | 'inheritance' | 'sale' | 'other';
  documents: string[];
  provider: 'EON' | 'ELMU' | 'DEMASZ' | 'OPUS' | 'MVMI';
}

interface PODRegistrationRequest {
  podId: string;
  customerName: string;
  customerAddress: string;
  customerPhone: string;
  customerEmail: string;
  connectionType: 'new' | 'modification' | 'reconnection';
  meterType: 'residential' | 'commercial' | 'industrial';
  capacity: number;
  voltage: '230V' | '400V' | 'high_voltage';
  installationDate: string;
  provider: 'EON' | 'ELMU' | 'DEMASZ' | 'OPUS' | 'MVMI';
  technicalData: {
    coordinates: string;
    buildingType: string;
    accessInfo: string;
  };
}

interface ContractRequest {
  id?: string;
  customerId: string;
  customerName: string;
  podId: string;
  contractType: 'new' | 'modification' | 'renewal' | 'termination' | 'live';
  contractNumber: string;
  startDate: string;
  endDate: string;
  provider: 'EON' | 'ELMU' | 'DEMASZ' | 'OPUS' | 'MVMI';
  tariffType: 'residential' | 'commercial' | 'industrial';
  capacity: number;
  pricePackage: string;
  monthlyFee: number;
  unitPrice: number;
  status: 'draft' | 'active' | 'pending' | 'expired' | 'terminated';
  notes: string;
}

const EnergiaUTILMD = () => {
  const { t } = useLanguage();
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [showPODForm, setShowPODForm] = useState(false);
  const [showCustomerTransfer, setShowCustomerTransfer] = useState(false);
  const [showPODRegistration, setShowPODRegistration] = useState(false);
  const [showContractManagement, setShowContractManagement] = useState(false);
  const [podRequest, setPodRequest] = useState<PODChangeRequest>({
    podId: '',
    customerName: '',
    customerAddress: '',
    contractStartDate: '',
    contractEndDate: '',
    messageType: 'C01',
    provider: 'EON',
    newTrader: '15X-ENERGIA-INFO-T',
    oldTrader: '',
    attachments: []
  });
  const [customerTransferRequest, setCustomerTransferRequest] = useState<CustomerTransferRequest>({
    podId: '',
    currentCustomerName: '',
    currentCustomerAddress: '',
    newCustomerName: '',
    newCustomerAddress: '',
    newCustomerPhone: '',
    newCustomerEmail: '',
    transferDate: '',
    transferReason: 'ownership_change',
    documents: [],
    provider: 'EON'
  });
  const [podRegistrationRequest, setPodRegistrationRequest] = useState<PODRegistrationRequest>({
    podId: '',
    customerName: '',
    customerAddress: '',
    customerPhone: '',
    customerEmail: '',
    connectionType: 'new',
    meterType: 'residential',
    capacity: 0,
    voltage: '230V',
    installationDate: '',
    provider: 'EON',
    technicalData: {
      coordinates: '',
      buildingType: '',
      accessInfo: ''
    }
  });
  const [generatedXML, setGeneratedXML] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [utilmdFormat, setUtilmdFormat] = useState<UTILMDFormat>('ISU_UTILMD');
  const [marketSettings, setMarketSettings] = useState<UTILMDMarketSettings>({
    format: 'ISU_UTILMD',
    market: 'HU',
    version: '3.0',
    encoding: 'UTF-8'
  });
  const [contractRequest, setContractRequest] = useState<ContractRequest>({
    customerId: '',
    customerName: '',
    podId: '',
    contractType: 'new',
    contractNumber: '',
    startDate: '',
    endDate: '',
    provider: 'EON',
    tariffType: 'residential',
    capacity: 0,
    pricePackage: '',
    monthlyFee: 0,
    unitPrice: 0,
    status: 'draft',
    notes: ''
  });
  const [contracts, setContracts] = useState<ContractRequest[]>([]);
  
  // Real-time UTILMD data
  const {
    utilmdData,
    c01Requests,
    loading,
    error,
    refreshData,
    getPendingC01Requests,
    getRecentActivity
  } = useUTILMD();

  const features: UTILMDFeature[] = [
    {
      id: 'pod_change',
      title: 'Új POD Változás',
      description: 'Kereskedőváltás és új bekapcsolás UTILMD üzenetek generálása',
      icon: PlusIcon,
      status: 'development',
      estimatedDate: ''
    },
    {
      id: 'customer_transfer',
      title: 'Fogyasztó Átírás',
      description: 'Automatikus fogyasztó átírási folyamat UTILMD protokoll használatával (SZT-03)',
      icon: UserGroupIcon,
      status: 'development',
      estimatedDate: ''
    },
    {
      id: 'pod_registration',
      title: 'POD Regisztráció',
      description: 'Új mérési pontok regisztrálása és meglévők módosítása',
      icon: DocumentTextIcon,
      status: 'development',
      estimatedDate: ''
    },
    {
      id: 'contract_management',
      title: 'Szerződés Kezelés',
      description: 'Energiaszerződések életciklus-kezelése és automatikus frissítése',
      icon: BuildingOffice2Icon,
      status: 'planned',
      estimatedDate: ''
    },
    {
      id: 'billing_transition',
      title: 'Számlázás Átállítás',
      description: 'Automatikus számlázási átállítás új tulajdonosra',
      icon: ArrowPathIcon,
      status: 'planned',
      estimatedDate: ''
    },
    {
      id: 'compliance_check',
      title: 'Jogosultság Ellenőrzés',
      description: 'Automatikus jogosultság-ellenőrzés és dokumentum validáció',
      icon: CheckCircleIcon,
      status: 'development',
      estimatedDate: ''
    },
    {
      id: 'notification_system',
      title: 'Értesítési Rendszer',
      description: 'Automatikus értesítések az átírási folyamat minden lépéséről',
      icon: SparklesIcon,
      status: 'planned',
      estimatedDate: ''
    }
  ];

  const generateUTILMDXML = (request: PODChangeRequest): string => {
    try {
      const generator = new UTILMDGenerator({
        format: utilmdFormat,
        marketSettings: marketSettings,
        validation: {
          strictPODFormat: true,
          validateEICCodes: true,
          checkDateRanges: true
        },
        output: {
          prettifyXML: true,
          addComments: false,
          includeTimestamp: true
        }
      });
      
      return generator.generateXML(request);
    } catch (error) {
      console.error('UTILMD XML generálási hiba:', error);
      throw new Error(`XML generálás sikertelen: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`);
    }
  };



  const generateUTILMDFileName = (request: PODChangeRequest): string => {
    try {
      const generator = new UTILMDGenerator({
        format: utilmdFormat,
        marketSettings: marketSettings
      });
      
      return generator.generateFileName(request);
    } catch (error) {
      console.error('UTILMD fájlnév generálási hiba:', error);
      // Fallback fájlnév
      const now = new Date();
      const dateStr = now.toISOString().substring(0, 10).replace(/-/g, '');
      const timeStr = now.toISOString().substring(11, 19).replace(/:/g, '');
      return `UTILMD_${request.provider}_${dateStr}_${timeStr}_001.xml`;
    }
  };

  const validatePODRequest = (request: PODChangeRequest): string[] => {
    try {
      const generator = new UTILMDGenerator({
        format: utilmdFormat,
        marketSettings: marketSettings,
        validation: {
          strictPODFormat: true,
          validateEICCodes: true,
          checkDateRanges: true
        }
      });
      
      return generator.validateRequest(request);
    } catch (error) {
      console.error('UTILMD validálási hiba:', error);
      // Fallback validáció
      const errors: string[] = [];
      
      if (!request.podId.match(/^HU\d{6}-\d{2}-S\d{20}$/)) {
        errors.push('POD azonosító formátuma hibás. Helyes formátum: HU000000-00-S00000000000000000000');
      }
      
      if (!request.customerName.trim()) {
        errors.push('Ügyfél neve kötelező');
      }
      
      if (!request.customerAddress.trim()) {
        errors.push('Ügyfél címe kötelező');
      }
      
      return errors;
    }
  };

  const validateCustomerTransfer = (request: CustomerTransferRequest): string[] => {
    const errors: string[] = [];
    
    // POD formátum ellenőrzés
    if (!request.podId.match(/^HU\d{6}-\d{2}-S\d{20}$/)) {
      errors.push('POD azonosító formátuma hibás. Helyes formátum: HU000000-00-S00000000000000000000');
    }
    
    if (!request.currentCustomerName.trim()) {
      errors.push('Jelenlegi ügyfél neve kötelező');
    }
    
    if (!request.currentCustomerAddress.trim()) {
      errors.push('Jelenlegi ügyfél címe kötelező');
    }
    
    if (!request.newCustomerName.trim()) {
      errors.push('Új ügyfél neve kötelező');
    }
    
    if (!request.newCustomerAddress.trim()) {
      errors.push('Új ügyfél címe kötelező');
    }
    
    if (!request.newCustomerPhone.trim()) {
      errors.push('Új ügyfél telefonszáma kötelező');
    }
    
    if (!request.newCustomerEmail.trim()) {
      errors.push('Új ügyfél email címe kötelező');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(request.newCustomerEmail)) {
      errors.push('Új ügyfél email címe nem érvényes');
    }
    
    if (!request.transferDate) {
      errors.push('Átírás dátuma kötelező');
    }
    
    if (request.transferDate) {
      const transferDate = new Date(request.transferDate);
      const today = new Date();
      if (transferDate < today) {
        errors.push('Átírás dátuma nem lehet múltbeli');
      }
    }
    
    return errors;
  };

  const generateCustomerTransferXML = (request: CustomerTransferRequest): string => {
    const now = new Date();
    const timestamp = now.toISOString().replace(/[-:]/g, '').substring(0, 15);
    const referenceNumber = `TRANSFER_${request.provider}_${request.podId}_${timestamp}`;
    
    // SZT-03 folyamat szerinti UTILMD XML generálás (Z01 üzenet)
    return `<?xml version="1.0" encoding="UTF-8"?>
<UTILMD xmlns="urn:entsoe.eu:wgedi:utilmd:iec62325:351:3:utilmd-marketdocument:3:0">
  <UNH>
    <S001>
      <E0062>UTILMD</E0062>
      <E0065>D</E0065>
      <E0052>03B</E0052>
      <E0054>UN</E0054>
      <E0051>EAN008</E0051>
    </S001>
    <E0062>${referenceNumber}</E0062>
  </UNH>
  
  <UNB>
    <S001>
      <E0001>UNOC</E0001>
      <E0002>3</E0002>
    </S001>
    <S002>
      <E0004>15X-ENERGIA-INFO-T</E0004>
      <E0007>14</E0007>
    </S002>
    <S003>
      <E0010>${request.provider === 'EON' ? 'HU000130F11-T' : request.provider === 'ELMU' ? 'HU000210F11-T' : request.provider === 'DEMASZ' ? 'HU000310F11-T' : request.provider === 'OPUS' ? 'HU000410F11-T' : 'HU000510F11-T'}</E0010>
      <E0007>14</E0007>
    </S003>
    <S004>
      <E0017>${now.toISOString().substring(0, 8)}</E0017>
      <E0019>${now.toISOString().substring(11, 15)}</E0019>
    </S004>
    <E0020>${referenceNumber}</E0020>
  </UNB>
  
  <BGM>
    <C002>
      <E1001>Z01</E1001>
      <E1131>ZZZ</E1131>
    </C002>
    <E1004>${referenceNumber}</E1004>
    <E1225>7</E1225>
  </BGM>
  
  <DTM>
    <C507>
      <E2005>137</E2005>
      <E2380>${now.toISOString().substring(0, 8)}</E2380>
      <E2379>102</E2379>
    </C507>
  </DTM>
  
  <RFF>
    <C506>
      <E1153>Z13</E1153>
      <E1154>${request.podId}</E1154>
    </C506>
  </RFF>
  
  <NAD>
    <E3035>MS</E3035>
    <C082>
      <E3039>15X-ENERGIA-INFO-T</E3039>
      <E3055>9</E3055>
    </C082>
  </NAD>
  
  <NAD>
    <E3035>MR</E3035>
    <C082>
      <E3039>${request.provider === 'EON' ? 'HU000130F11-T' : request.provider === 'ELMU' ? 'HU000210F11-T' : request.provider === 'DEMASZ' ? 'HU000310F11-T' : request.provider === 'OPUS' ? 'HU000410F11-T' : 'HU000510F11-T'}</E3039>
      <E3055>9</E3055>
    </C082>
  </NAD>
  
  <UNS>
    <E0081>D</E0081>
  </UNS>
  
  <!-- Régi ügyfél adatok -->
  <NAD>
    <E3035>IV</E3035>
    <C080>
      <E3036>${request.currentCustomerName}</E3036>
    </C080>
    <C059>
      <E3042>${request.currentCustomerAddress}</E3042>
    </C059>
  </NAD>
  
  <!-- Új ügyfél adatok -->
  <NAD>
    <E3035>CN</E3035>
    <C080>
      <E3036>${request.newCustomerName}</E3036>
    </C080>
    <C059>
      <E3042>${request.newCustomerAddress}</E3042>
    </C059>
  </NAD>
  
  <LOC>
    <E3227>172</E3227>
    <C517>
      <E3225>${request.podId}</E3225>
      <E3055>9</E3055>
    </C517>
  </LOC>
  
  <DTM>
    <C507>
      <E2005>194</E2005>
      <E2380>${request.transferDate.replace(/-/g, '')}</E2380>
      <E2379>102</E2379>
    </C507>
  </DTM>
  
  <RFF>
    <C506>
      <E1153>Z02</E1153>
      <E1154>${request.transferReason}</E1154>
    </C506>
  </RFF>
  
  <UNT>
    <E0074>1</E0074>
    <E0062>${referenceNumber}</E0062>
  </UNT>
</UTILMD>`;
  };

  const handleGenerateXML = () => {
    const errors = validatePODRequest(podRequest);
    setValidationErrors(errors);
    
    if (errors.length === 0) {
      const xml = generateUTILMDXML(podRequest);
      setGeneratedXML(xml);
    } else {
      setGeneratedXML('');
    }
  };

  const handleDownloadXML = () => {
    const blob = new Blob([generatedXML], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = generateUTILMDFileName(podRequest);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUploadToServer = async () => {
    try {
      if (!generatedXML) {
        alert('Nincs generált XML fájl.');
        return;
      }

      setIsUploading(true);
      const fileName = generateUTILMDFileName(podRequest);
      const fileRef = ref(storage, `utilmd_files/${fileName}`);

      // XML feltöltése Firebase Storage-ba
      await uploadBytes(fileRef, new Blob([generatedXML], { type: 'application/xml' }));
      const downloadURL = await getDownloadURL(fileRef);

      // Metaadatok mentése Firestore-ba
      await addDoc(collection(db, 'utilmd_requests'), {
        fileName,
        downloadURL,
        podId: podRequest.podId,
        customerName: podRequest.customerName,
        customerAddress: podRequest.customerAddress,
        contractStartDate: podRequest.contractStartDate,
        contractEndDate: podRequest.contractEndDate,
        messageType: podRequest.messageType,
        provider: podRequest.provider,
        newTrader: podRequest.newTrader,
        oldTrader: podRequest.oldTrader,
        status: 'uploaded',
        createdAt: serverTimestamp(),
        createdBy: 'user' // Itt lehetne az aktuális felhasználó ID-ja
      });

      alert(`XML sikeresen feltöltve és mentve! Fájl: ${fileName}`);
      setShowPODForm(false);
      setPodRequest({
        podId: '',
        customerName: '',
        customerAddress: '',
        contractStartDate: '',
        contractEndDate: '',
        messageType: 'C01',
        provider: 'EON',
        newTrader: '15X-ENERGIA-INFO-T',
        oldTrader: '',
        attachments: []
      });
      setGeneratedXML('');
      setValidationErrors([]);
      refreshData();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Hiba történt a feltöltés során!');
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'checked':
        return 'text-green-600';
      case 'no_access':
        return 'text-orange-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'checked':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'no_access':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'checked':
        return 'Ellenőrizve';
      case 'no_access':
        return 'Nincs hozzáférés';
      case 'error':
        return 'Hiba';
      default:
        return 'Várakozás';
    }
  };

  // Ha nincs adat, loading állapot
  if (loading && !utilmdData) {
    return <LoadingScreen />;
  }

  // Ha nincs adat és nem loading, hiba üzenet
  if (!utilmdData && !loading) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <XCircleIcon className="h-6 w-6 text-red-500" />
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-800">
                Nincs UTILMD adat
              </h3>
              <p className="text-red-600">
                {error || 'A UTILMD monitoring rendszer még nem futott le. Kérjük futtassa a Python szkriptet.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pendingC01 = getPendingC01Requests();
  const recentActivity = getRecentActivity();

  return (
    <div className="space-y-6">
      {/* Header */}
      <CommonPageHeader
        title="Energia UTILMD"
        subtitle="Fogyasztó átírási rendszer"
        icon={DocumentTextIcon}
        onRefresh={refreshData}
      >
        <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span>EON</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span>ELMU</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span>DEMASZ</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span>OPUS</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span>MVMI</span>
            </div>
          </div>
        </div>
      </CommonPageHeader>









      {/* Features Overview */}
      <div className="bg-white rounded-xl shadow-md">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center">
            <SparklesIcon className="w-5 h-5 mr-2 text-indigo-600" />
            UTILMD Funkciók
          </h2>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {features.map((feature) => {
              const getFeatureColors = (id: string) => {
                switch (id) {
                  case 'pod_change':
                    return {
                      bg: 'bg-gradient-to-br from-indigo-50 to-purple-50',
                      border: 'border-indigo-200 hover:border-indigo-400',
                      iconBg: 'bg-indigo-100',
                      iconColor: 'text-indigo-600'
                    };
                  case 'customer_transfer':
                    return {
                      bg: 'bg-gradient-to-br from-green-50 to-emerald-50',
                      border: 'border-green-200 hover:border-green-400',
                      iconBg: 'bg-green-100',
                      iconColor: 'text-green-600'
                    };
                  case 'pod_registration':
                    return {
                      bg: 'bg-gradient-to-br from-blue-50 to-cyan-50',
                      border: 'border-blue-200 hover:border-blue-400',
                      iconBg: 'bg-blue-100',
                      iconColor: 'text-blue-600'
                    };
                  case 'contract_management':
                    return {
                      bg: 'bg-gradient-to-br from-orange-50 to-amber-50',
                      border: 'border-orange-200 hover:border-orange-400',
                      iconBg: 'bg-orange-100',
                      iconColor: 'text-orange-600'
                    };
                  case 'billing_transition':
                    return {
                      bg: 'bg-gradient-to-br from-purple-50 to-pink-50',
                      border: 'border-purple-200 hover:border-purple-400',
                      iconBg: 'bg-purple-100',
                      iconColor: 'text-purple-600'
                    };
                  case 'compliance_check':
                    return {
                      bg: 'bg-gradient-to-br from-teal-50 to-cyan-50',
                      border: 'border-teal-200 hover:border-teal-400',
                      iconBg: 'bg-teal-100',
                      iconColor: 'text-teal-600'
                    };
                  case 'notification_system':
                    return {
                      bg: 'bg-gradient-to-br from-rose-50 to-pink-50',
                      border: 'border-rose-200 hover:border-rose-400',
                      iconBg: 'bg-rose-100',
                      iconColor: 'text-rose-600'
                    };
                  default:
                    return {
                      bg: 'bg-gray-50',
                      border: 'border-gray-200 hover:border-gray-400',
                      iconBg: 'bg-gray-100',
                      iconColor: 'text-gray-600'
                    };
                }
              };

              const colors = getFeatureColors(feature.id);

              return (
              <div
                key={feature.id}
                  className={`border rounded-lg p-3 cursor-pointer transition-all duration-200 hover:shadow-md ${colors.bg} ${colors.border}`}
                  onClick={() => {
                    if (feature.id === 'pod_change') {
                      setShowPODForm(true);
                    } else if (feature.id === 'customer_transfer') {
                      setShowCustomerTransfer(true);
                    } else if (feature.id === 'pod_registration') {
                      setShowPODRegistration(true);
                    } else if (feature.id === 'contract_management') {
                      setShowContractManagement(true);
                    }
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${colors.iconBg}`}>
                      <feature.icon className={`w-5 h-5 ${colors.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-800 text-sm">{feature.title}</h3>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{feature.description}</p>
                      </div>
                    </div>
                  </div>
              );
            })}
          </div>
        </div>
      </div>





      {/* Összesítő kártyák */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ServerIcon className="h-5 w-5 text-gray-400" />
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-xs font-medium text-gray-500 truncate">
                    Összes Provider
                  </dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {utilmdData?.summary.totalProviders || 5}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-xs font-medium text-gray-500 truncate">
                    Aktív Providerek
                  </dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {utilmdData?.summary.activeProviders || 4}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-xs font-medium text-gray-500 truncate">
                    UTILMD Fájlok
                  </dt>
                  <dd className="text-sm font-medium text-gray-900">
                    0
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-xs font-medium text-gray-500 truncate">
                    Utolsó Ellenőrzés
                  </dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {new Date().toLocaleTimeString('hu-HU', {hour: '2-digit', minute: '2-digit'})}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Provider státuszok */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            Provider Státuszok
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {utilmdData && Object.entries(utilmdData.providers).map(([providerName, provider]) => (
              <div
                key={providerName}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(provider.status)}
                      <h4 className="text-sm font-medium text-gray-900">
                        {providerName}
                      </h4>
                    </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    provider.status === 'checked' ? 'bg-green-100 text-green-800' :
                    provider.status === 'no_access' ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                        {getStatusText(provider.status)}
                  </span>
                    </div>
                    
                    {provider.status !== 'no_access' && (
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                      <span className="font-medium">{provider.utilmdFiles}</span> fájl
                          </div>
                    <div>
                      <span className={`font-medium ${provider.c01Requests > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                            {provider.c01Requests}
                      </span> C01
                          </div>
                        </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legutóbbi aktivitás */}
      {recentActivity.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Legutóbbi Aktivitás
            </h3>
            
            <div className="space-y-3">
              {recentActivity.slice(0, 10).map((activity, index) => (
                <div key={index} className="flex items-center space-x-3 text-sm">
                  <div className={`w-2 h-2 rounded-full ${
                    activity.status === 'success' ? 'bg-green-400' :
                    activity.status === 'error' ? 'bg-red-400' :
                    activity.status === 'pending' ? 'bg-yellow-400' :
                    'bg-blue-400'
                  }`}></div>
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">{activity.provider}</span>
                    <span className="text-gray-600 ml-2">{activity.message}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(activity.timestamp).toLocaleString('hu-HU')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}



      {/* Hibakezelés */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Hiba történt
            </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* POD Változás Rögzítő Modal */}
      {showPODForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <PlusIcon className="w-6 h-6 mr-3" />
                  Új POD Változás Rögzítése
                </h3>
                <button
                  onClick={() => setShowPODForm(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
              <p className="text-indigo-100 text-sm mt-2">
                {utilmdFormat === 'ISU_UTILMD' 
                  ? '🇭🇺 Magyar UTILMD (ISU_UTILMD) szabvány szerinti POD változás XML generálása'
                  : '🌍 EU UTILMD (EDIFACT) szabvány szerinti POD változás XML generálása'
                }
              </p>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Űrlap */}
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
                      <h4 className="text-sm font-medium text-blue-800">Üzenet információ</h4>
          </div>
                    <p className="text-sm text-blue-700 mt-2">
                      {utilmdFormat === 'ISU_UTILMD' 
                        ? 'Az XML fájl automatikusan generálódik a magyar ISU_UTILMD (SAP) szabvány szerint.'
                        : 'Az XML fájl automatikusan generálódik az EU EDIFACT UTILMD szabvány szerint.'
                      }
                    </p>
        </div>

                  {/* Formátumválasztó */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      UTILMD Formátum
                    </h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="utilmdFormat"
                            value="ISU_UTILMD"
                            checked={utilmdFormat === 'ISU_UTILMD'}
                            onChange={(e) => {
                              setUtilmdFormat(e.target.value as UTILMDFormat);
                              setMarketSettings({
                                format: 'ISU_UTILMD',
                                market: 'HU',
                                version: '3.0',
                                encoding: 'UTF-8'
                              });
                            }}
                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          />
                          <span className="text-sm font-medium text-gray-900">
                            🇭🇺 Magyar piac (ISU_UTILMD)
                          </span>
                        </label>
                        <p className="ml-7 text-xs text-gray-500">
                          SAP alapú formátum, kompatibilis a mintafájlokkal
                        </p>
                      </div>
                      
                      <div>
                        <label className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="utilmdFormat"
                            value="EDIFACT"
                            checked={utilmdFormat === 'EDIFACT'}
                            onChange={(e) => {
                              setUtilmdFormat(e.target.value as UTILMDFormat);
                              setMarketSettings({
                                format: 'EDIFACT',
                                market: 'EU',
                                version: '3.0',
                                encoding: 'UTF-8'
                              });
                            }}
                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                          />
                          <span className="text-sm font-medium text-gray-900">
                            🌍 EU piac (EDIFACT)
                          </span>
                        </label>
                        <p className="ml-7 text-xs text-gray-500">
                          Standard EDIFACT UTILMD formátum
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Alapadatok
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Üzenet típus</label>
                      <select
                        value={podRequest.messageType}
                        onChange={(e) => setPodRequest({...podRequest, messageType: e.target.value as 'C01' | 'C03' | 'E01'})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                      >
                        <option value="C01">C01 - Kereskedőváltás Bejelentés (SZT-04)</option>
                        <option value="C03">C03 - Kereskedőváltás Elfogadás (SZT-04)</option>
                        <option value="E01">E01 - Új Bekapcsolás (SZT-01)</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        {podRequest.messageType === 'C01' && 'Új kereskedő kezdeményezi a kereskedőváltást (T-30 nappal előre)'}
                        {podRequest.messageType === 'C03' && 'Régi kereskedő elfogadja a kereskedőváltást'}
                        {podRequest.messageType === 'E01' && 'Új felhasználási hely bekapcsolása'}
                      </p>
      </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                      <select
                        value={podRequest.provider}
                        onChange={(e) => setPodRequest({...podRequest, provider: e.target.value as any})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                      >
                        <option value="EON">EON - E.ON Földgáz Trade Zrt.</option>
                        <option value="ELMU">ELMU - ELMŰ Hálózati Kft.</option>
                        <option value="DEMASZ">DEMASZ - DÉMÁSZ Hálózati Kft.</option>
                        <option value="OPUS">OPUS - OPUS TITÁSZ Hálózati Kft.</option>
                        <option value="MVMI">MVMI - MVM Főgáz Hálózati Kft.</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      POD és Ügyfél adatok
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">POD Azonosító</label>
                      <input
                        type="text"
                        value={podRequest.podId}
                        onChange={(e) => setPodRequest({...podRequest, podId: e.target.value})}
                        placeholder="HU000130-11-S00000000000000012251"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Formátum: HU000000-00-S00000000000000000000</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ügyfél neve</label>
                      <input
                        type="text"
                        value={podRequest.customerName}
                        onChange={(e) => setPodRequest({...podRequest, customerName: e.target.value})}
                        placeholder="Teszt Ügyfél Kft."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ügyfél címe</label>
                      <input
                        type="text"
                        value={podRequest.customerAddress}
                        onChange={(e) => setPodRequest({...podRequest, customerAddress: e.target.value})}
                        placeholder="1234 Budapest, Kossuth utca 1."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Szerződés adatok
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Szerződés kezdete</label>
                        <input
                          type="date"
                          value={podRequest.contractStartDate}
                          onChange={(e) => setPodRequest({...podRequest, contractStartDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Szerződés vége</label>
                        <input
                          type="date"
                          value={podRequest.contractEndDate}
                          onChange={(e) => setPodRequest({...podRequest, contractEndDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Új kereskedő EIC</label>
                      <input
                        type="text"
                        value={podRequest.newTrader}
                        onChange={(e) => setPodRequest({...podRequest, newTrader: e.target.value})}
                        placeholder="15X-ENERGIA-INFO-T"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Formátum: 16 karakter EIC kód</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Régi kereskedő EIC (opcionális)</label>
                      <input
                        type="text"
                        value={podRequest.oldTrader}
                        onChange={(e) => setPodRequest({...podRequest, oldTrader: e.target.value})}
                        placeholder="15X-OLD-TRADER---T"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Validációs hibák megjelenítése */}
                  {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Validációs hibák:</h3>
                          <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                            {validationErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* XML Előnézet */}
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-800 flex items-center">
                        <CodeBracketIcon className="w-5 h-5 mr-2 text-indigo-600" />
                        {utilmdFormat === 'ISU_UTILMD' 
                          ? '🇭🇺 ISU_UTILMD XML Előnézet'
                          : '🌍 EDIFACT XML Előnézet'
                        }
                      </h4>
                      {generatedXML && (
                        <div className="flex space-x-2">
                          <button
                            onClick={handleDownloadXML}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                            Letöltés
                          </button>
                          <button
                            onClick={handleUploadToServer}
                            disabled={isUploading}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            {isUploading ? (
                              <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                            )}
                            {isUploading ? 'Feltöltés...' : 'Feltöltés'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="border border-gray-200 rounded-lg bg-gray-50 h-96 overflow-y-auto">
                      {generatedXML ? (
                        <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono p-4 leading-relaxed">
                          {generatedXML}
                        </pre>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <CodeBracketIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-sm font-medium">XML Előnézet</p>
                            <p className="text-xs mt-1">Kattints az "XML Generálás" gombra az előnézet megtekintéséhez</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {utilmdFormat === 'ISU_UTILMD' ? (
                    <>
                      <span className="font-medium">🇭🇺 Magyar ISU_UTILMD</span> - SAP kompatibilis
                    </>
                  ) : (
                    <>
                      <span className="font-medium">🌍 EU EDIFACT UTILMD</span> - EASEE Gas kompatibilis
                    </>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowPODForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Mégse
                  </button>
                  <button
                    onClick={handleGenerateXML}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <CodeBracketIcon className="w-4 h-4 mr-2 inline" />
                    XML Generálás
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fogyasztó Átírás Modal */}
      {showCustomerTransfer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <UserGroupIcon className="w-6 h-6 mr-3" />
                  Fogyasztó Átírás (SZT-03)
              </h3>
                <button
                  onClick={() => setShowCustomerTransfer(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
              <p className="text-green-100 text-sm mt-2">
                Felhasználó változás kezelése UTILMD Z01 üzenet generálásával
              </p>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Űrlap */}
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <InformationCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                      <h4 className="text-sm font-medium text-green-800">SZT-03 Folyamat</h4>
                    </div>
                    <p className="text-sm text-green-700 mt-2">
                      Felhasználó változás kezelése - 15 napon belül bejelentendő a változás.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Alapadatok
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                      <select
                        value={customerTransferRequest.provider}
                        onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, provider: e.target.value as any})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 bg-white"
                      >
                        <option value="EON">EON - E.ON Földgáz Trade Zrt.</option>
                        <option value="ELMU">ELMU - ELMŰ Hálózati Kft.</option>
                        <option value="DEMASZ">DEMASZ - DÉMÁSZ Hálózati Kft.</option>
                        <option value="OPUS">OPUS - OPUS TITÁSZ Hálózati Kft.</option>
                        <option value="MVMI">MVMI - MVM Főgáz Hálózati Kft.</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">POD Azonosító</label>
                      <input
                        type="text"
                        value={customerTransferRequest.podId}
                        onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, podId: e.target.value})}
                        placeholder="HU000130-11-S00000000000000012251"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Formátum: HU000000-00-S00000000000000000000</p>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Jelenlegi Ügyfél Adatok
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Jelenlegi ügyfél neve</label>
                      <input
                        type="text"
                        value={customerTransferRequest.currentCustomerName}
                        onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, currentCustomerName: e.target.value})}
                        placeholder="Régi Ügyfél Kft."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Jelenlegi ügyfél címe</label>
                      <input
                        type="text"
                        value={customerTransferRequest.currentCustomerAddress}
                        onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, currentCustomerAddress: e.target.value})}
                        placeholder="1234 Budapest, Régi utca 1."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Új Ügyfél Adatok
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Új ügyfél neve</label>
                      <input
                        type="text"
                        value={customerTransferRequest.newCustomerName}
                        onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, newCustomerName: e.target.value})}
                        placeholder="Új Ügyfél Kft."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Új ügyfél címe</label>
                      <input
                        type="text"
                        value={customerTransferRequest.newCustomerAddress}
                        onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, newCustomerAddress: e.target.value})}
                        placeholder="1234 Budapest, Új utca 1."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Telefonszám</label>
                        <input
                          type="tel"
                          value={customerTransferRequest.newCustomerPhone}
                          onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, newCustomerPhone: e.target.value})}
                          placeholder="+36 30 123 4567"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email cím</label>
                        <input
                          type="email"
                          value={customerTransferRequest.newCustomerEmail}
                          onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, newCustomerEmail: e.target.value})}
                          placeholder="uj.ugyfel@example.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Átírás Részletei
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Átírás dátuma</label>
                        <input
                          type="date"
                          value={customerTransferRequest.transferDate}
                          onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, transferDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Átírás oka</label>
                        <select
                          value={customerTransferRequest.transferReason}
                          onChange={(e) => setCustomerTransferRequest({...customerTransferRequest, transferReason: e.target.value as any})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 bg-white"
                        >
                          <option value="ownership_change">Tulajdonosváltás</option>
                          <option value="inheritance">Öröklés</option>
                          <option value="sale">Eladás</option>
                          <option value="other">Egyéb</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Validációs hibák megjelenítése */}
                  {validationErrors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Validációs hibák:</h3>
                          <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                            {validationErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* XML Előnézet */}
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-800 flex items-center">
                        <CodeBracketIcon className="w-5 h-5 mr-2 text-green-600" />
                        Z01 Üzenet Előnézet
                      </h4>
                      {generatedXML && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              const blob = new Blob([generatedXML], { type: 'application/xml' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `TRANSFER_${customerTransferRequest.provider}_${customerTransferRequest.podId}_${new Date().toISOString().split('T')[0]}.xml`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            }}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                            Letöltés
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                setIsUploading(true);
                                const fileName = `TRANSFER_${customerTransferRequest.provider}_${customerTransferRequest.podId}_${new Date().toISOString().replace(/[-:]/g, '').substring(0, 15)}.xml`;
                                const fileRef = ref(storage, `utilmd_transfers/${fileName}`);
                                
                                await uploadBytes(fileRef, new Blob([generatedXML], { type: 'application/xml' }));
                                const downloadURL = await getDownloadURL(fileRef);
                                
                                await addDoc(collection(db, 'utilmd_transfers'), {
                                  fileName,
                                  downloadURL,
                                  podId: customerTransferRequest.podId,
                                  currentCustomerName: customerTransferRequest.currentCustomerName,
                                  newCustomerName: customerTransferRequest.newCustomerName,
                                  transferDate: customerTransferRequest.transferDate,
                                  transferReason: customerTransferRequest.transferReason,
                                  provider: customerTransferRequest.provider,
                                  status: 'uploaded',
                                  createdAt: serverTimestamp(),
                                  createdBy: 'user'
                                });
                                
                                alert(`Átírás sikeresen feltöltve! Fájl: ${fileName}`);
                                setShowCustomerTransfer(false);
                                setCustomerTransferRequest({
                                  podId: '',
                                  currentCustomerName: '',
                                  currentCustomerAddress: '',
                                  newCustomerName: '',
                                  newCustomerAddress: '',
                                  newCustomerPhone: '',
                                  newCustomerEmail: '',
                                  transferDate: '',
                                  transferReason: 'ownership_change',
                                  documents: [],
                                  provider: 'EON'
                                });
                                setGeneratedXML('');
                                setValidationErrors([]);
                              } catch (error) {
                                console.error('Upload error:', error);
                                alert('Hiba történt a feltöltés során!');
                              } finally {
                                setIsUploading(false);
                              }
                            }}
                            disabled={isUploading}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            {isUploading ? (
                              <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                            )}
                            {isUploading ? 'Feltöltés...' : 'Feltöltés'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="border border-gray-200 rounded-lg bg-gray-50 h-96 overflow-y-auto">
                      {generatedXML ? (
                        <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono p-4 leading-relaxed">
                          {generatedXML}
                        </pre>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <CodeBracketIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-sm font-medium">Z01 Üzenet Előnézet</p>
                            <p className="text-xs mt-1">Kattints az "XML Generálás" gombra az előnézet megtekintéséhez</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">SZT-03 Felhasználó változás</span> - 15 napon belül bejelentendő
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowCustomerTransfer(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Mégse
                  </button>
                  <button
                    onClick={() => {
                      const errors = validateCustomerTransfer(customerTransferRequest);
                      setValidationErrors(errors);
                      
                      if (errors.length === 0) {
                        const xml = generateCustomerTransferXML(customerTransferRequest);
                        setGeneratedXML(xml);
                      } else {
                        setGeneratedXML('');
                      }
                    }}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CodeBracketIcon className="w-4 h-4 mr-2 inline" />
                    XML Generálás
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* POD Regisztráció Modal */}
      {showPODRegistration && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <DocumentTextIcon className="w-6 h-6 mr-3" />
                  POD Regisztráció (SZT-01)
                </h3>
                <button
                  onClick={() => setShowPODRegistration(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
              <p className="text-blue-100 text-sm mt-2">
                Új mérési pontok regisztrálása és meglévők módosítása UTILMD E01 üzenet generálásával
              </p>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Űrlap */}
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <InformationCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
                      <h4 className="text-sm font-medium text-blue-800">SZT-01 Folyamat</h4>
                    </div>
                    <p className="text-sm text-blue-700 mt-2">
                      Új bekapcsolás kezelése - mérési pont regisztrálása és műszaki feltételek biztosítása.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Alapadatok
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                      <select
                        value={podRegistrationRequest.provider}
                        onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, provider: e.target.value as any})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="EON">EON - E.ON Földgáz Trade Zrt.</option>
                        <option value="ELMU">ELMU - ELMŰ Hálózati Kft.</option>
                        <option value="DEMASZ">DEMASZ - DÉMÁSZ Hálózati Kft.</option>
                        <option value="OPUS">OPUS - OPUS TITÁSZ Hálózati Kft.</option>
                        <option value="MVMI">MVMI - MVM Főgáz Hálózati Kft.</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">POD Azonosító</label>
                      <input
                        type="text"
                        value={podRegistrationRequest.podId}
                        onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, podId: e.target.value})}
                        placeholder="HU000130-11-S00000000000000012251"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Formátum: HU000000-00-S00000000000000000000</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kapcsolat típusa</label>
                      <select
                        value={podRegistrationRequest.connectionType}
                        onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, connectionType: e.target.value as any})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="new">Új bekapcsolás</option>
                        <option value="modification">Módosítás</option>
                        <option value="reconnection">Visszakapcsolás</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Ügyfél Adatok
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ügyfél neve</label>
                      <input
                        type="text"
                        value={podRegistrationRequest.customerName}
                        onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, customerName: e.target.value})}
                        placeholder="Teszt Ügyfél Kft."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ügyfél címe</label>
                      <input
                        type="text"
                        value={podRegistrationRequest.customerAddress}
                        onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, customerAddress: e.target.value})}
                        placeholder="1234 Budapest, Példa utca 1."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Telefonszám</label>
                        <input
                          type="tel"
                          value={podRegistrationRequest.customerPhone}
                          onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, customerPhone: e.target.value})}
                          placeholder="+36 30 123 4567"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email cím</label>
                        <input
                          type="email"
                          value={podRegistrationRequest.customerEmail}
                          onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, customerEmail: e.target.value})}
                          placeholder="ugyfel@example.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Műszaki Adatok
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mérő típusa</label>
                        <select
                          value={podRegistrationRequest.meterType}
                          onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, meterType: e.target.value as any})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                        >
                          <option value="residential">Lakossági</option>
                          <option value="commercial">Kereskedelmi</option>
                          <option value="industrial">Ipari</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Feszültség</label>
                        <select
                          value={podRegistrationRequest.voltage}
                          onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, voltage: e.target.value as any})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                        >
                          <option value="230V">230V (Egyfázisú)</option>
                          <option value="400V">400V (Háromfázisú)</option>
                          <option value="high_voltage">Nagyfeszültség</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Kapacitás (kW)</label>
                        <input
                          type="number"
                          value={podRegistrationRequest.capacity}
                          onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, capacity: parseFloat(e.target.value) || 0})}
                          placeholder="10"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Telepítés dátuma</label>
                        <input
                          type="date"
                          value={podRegistrationRequest.installationDate}
                          onChange={(e) => setPodRegistrationRequest({...podRegistrationRequest, installationDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Koordináták</label>
                      <input
                        type="text"
                        value={podRegistrationRequest.technicalData.coordinates}
                        onChange={(e) => setPodRegistrationRequest({
                          ...podRegistrationRequest, 
                          technicalData: {...podRegistrationRequest.technicalData, coordinates: e.target.value}
                        })}
                        placeholder="47.4979, 19.0402"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Épület típusa</label>
                      <input
                        type="text"
                        value={podRegistrationRequest.technicalData.buildingType}
                        onChange={(e) => setPodRegistrationRequest({
                          ...podRegistrationRequest, 
                          technicalData: {...podRegistrationRequest.technicalData, buildingType: e.target.value}
                        })}
                        placeholder="Családi ház, lakóépület, irodaépület, stb."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Hozzáférési információk</label>
                      <textarea
                        value={podRegistrationRequest.technicalData.accessInfo}
                        onChange={(e) => setPodRegistrationRequest({
                          ...podRegistrationRequest, 
                          technicalData: {...podRegistrationRequest.technicalData, accessInfo: e.target.value}
                        })}
                        placeholder="Hozzáférési útvonal, különleges utasítások, stb."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Validációs hibák megjelenítése */}
                  {validationErrors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Validációs hibák:</h3>
                          <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                            {validationErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* XML Előnézet */}
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-800 flex items-center">
                        <CodeBracketIcon className="w-5 h-5 mr-2 text-blue-600" />
                        E01 Üzenet Előnézet
                      </h4>
                      {generatedXML && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              const blob = new Blob([generatedXML], { type: 'application/xml' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `POD_REG_${podRegistrationRequest.provider}_${podRegistrationRequest.podId}_${new Date().toISOString().split('T')[0]}.xml`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            }}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                            Letöltés
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                setIsUploading(true);
                                const fileName = `POD_REG_${podRegistrationRequest.provider}_${podRegistrationRequest.podId}_${new Date().toISOString().replace(/[-:]/g, '').substring(0, 15)}.xml`;
                                const fileRef = ref(storage, `utilmd_registrations/${fileName}`);
                                
                                await uploadBytes(fileRef, new Blob([generatedXML], { type: 'application/xml' }));
                                const downloadURL = await getDownloadURL(fileRef);
                                
                                await addDoc(collection(db, 'utilmd_registrations'), {
                                  fileName,
                                  downloadURL,
                                  podId: podRegistrationRequest.podId,
                                  customerName: podRegistrationRequest.customerName,
                                  connectionType: podRegistrationRequest.connectionType,
                                  meterType: podRegistrationRequest.meterType,
                                  capacity: podRegistrationRequest.capacity,
                                  installationDate: podRegistrationRequest.installationDate,
                                  provider: podRegistrationRequest.provider,
                                  status: 'uploaded',
                                  createdAt: serverTimestamp(),
                                  createdBy: 'user'
                                });
                                
                                alert(`POD regisztráció sikeresen feltöltve! Fájl: ${fileName}`);
                                setShowPODRegistration(false);
                                setPodRegistrationRequest({
                                  podId: '',
                                  customerName: '',
                                  customerAddress: '',
                                  customerPhone: '',
                                  customerEmail: '',
                                  connectionType: 'new',
                                  meterType: 'residential',
                                  capacity: 0,
                                  voltage: '230V',
                                  installationDate: '',
                                  provider: 'EON',
                                  technicalData: {
                                    coordinates: '',
                                    buildingType: '',
                                    accessInfo: ''
                                  }
                                });
                                setGeneratedXML('');
                                setValidationErrors([]);
                              } catch (error) {
                                console.error('Upload error:', error);
                                alert('Hiba történt a feltöltés során!');
                              } finally {
                                setIsUploading(false);
                              }
                            }}
                            disabled={isUploading}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            {isUploading ? (
                              <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <DocumentArrowUpIcon className="w-4 h-4 mr-1" />
                            )}
                            {isUploading ? 'Feltöltés...' : 'Feltöltés'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="border border-gray-200 rounded-lg bg-gray-50 h-96 overflow-y-auto">
                      {generatedXML ? (
                        <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono p-4 leading-relaxed">
                          {generatedXML}
                        </pre>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <CodeBracketIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-sm font-medium">E01 Üzenet Előnézet</p>
                            <p className="text-xs mt-1">Kattints az "XML Generálás" gombra az előnézet megtekintéséhez</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">SZT-01 Új bekapcsolás</span> - Mérési pont regisztrálása
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowPODRegistration(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Mégse
                  </button>
                  <button
                    onClick={() => {
                      // Itt lesz a validáció és XML generálás
                      const errors: string[] = [];
                      // Validáció implementálása...
                      setValidationErrors(errors);
                      
                      if (errors.length === 0) {
                        // XML generálás implementálása...
                        const xml = `<?xml version="1.0" encoding="UTF-8"?>
<!-- POD Registration XML - Coming Soon -->`;
                        setGeneratedXML(xml);
                      } else {
                        setGeneratedXML('');
                      }
                    }}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <CodeBracketIcon className="w-4 h-4 mr-2 inline" />
                    XML Generálás
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contract Management Modal */}
      {showContractManagement && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 to-amber-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white flex items-center">
                  <BuildingOffice2Icon className="w-6 h-6 mr-3" />
                  Energia Szerződés Kezelés
                </h3>
                <button
                  onClick={() => setShowContractManagement(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
              <p className="text-orange-100 text-sm mt-2">
                Energia szerződések teljes életciklus kezelése - új szerződések, módosítások, megújítások
              </p>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Új szerződés létrehozása */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <InformationCircleIcon className="w-5 h-5 text-orange-600 mr-2" />
                      <h4 className="text-sm font-medium text-orange-800">Energia Szerződés</h4>
                    </div>
                    <p className="text-sm text-orange-700 mt-2">
                      Új energia szerződés létrehozása vagy meglévő szerződés módosítása/megújítása.
                    </p>
                  </div>

                  {/* Alapadatok */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Alapadatok
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Szerződés típusa</label>
                        <select
                          value={contractRequest.contractType}
                          onChange={(e) => setContractRequest({...contractRequest, contractType: e.target.value as any})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 bg-white"
                        >
                          <option value="new">Új szerződés</option>
                          <option value="live">Élő szerződés</option>
                          <option value="modification">Módosítás</option>
                          <option value="renewal">Megújítás</option>
                          <option value="termination">Felmondás</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Szolgáltató</label>
                        <select
                          value={contractRequest.provider}
                          onChange={(e) => setContractRequest({...contractRequest, provider: e.target.value as any})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 bg-white"
                        >
                          <option value="EON">EON - E.ON Földgáz Trade Zrt.</option>
                          <option value="ELMU">ELMU - ELMŰ Hálózati Kft.</option>
                          <option value="DEMASZ">DEMASZ - DÉMÁSZ Hálózati Kft.</option>
                          <option value="OPUS">OPUS - OPUS TITÁSZ Hálózati Kft.</option>
                          <option value="MVMI">MVMI - MVM Főgáz Hálózati Kft.</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Szerződés száma</label>
                        <input
                          type="text"
                          value={contractRequest.contractNumber}
                          onChange={(e) => setContractRequest({...contractRequest, contractNumber: e.target.value})}
                          placeholder="EI-2024-001"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Státusz</label>
                        <select
                          value={contractRequest.status}
                          onChange={(e) => setContractRequest({...contractRequest, status: e.target.value as any})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 bg-white"
                        >
                          <option value="draft">Piszkozat</option>
                          <option value="active">Aktív</option>
                          <option value="pending">Függőben</option>
                          <option value="expired">Lejárt</option>
                          <option value="terminated">Felmondva</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Ügyfél és POD adatok */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Ügyfél és POD Adatok
                    </h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ügyfél azonosító</label>
                      <input
                        type="text"
                        value={contractRequest.customerId}
                        onChange={(e) => setContractRequest({...contractRequest, customerId: e.target.value})}
                        placeholder="CUST-001"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ügyfél neve</label>
                      <input
                        type="text"
                        value={contractRequest.customerName}
                        onChange={(e) => setContractRequest({...contractRequest, customerName: e.target.value})}
                        placeholder="Teszt Ügyfél Kft."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">POD Azonosító</label>
                      <input
                        type="text"
                        value={contractRequest.podId}
                        onChange={(e) => setContractRequest({...contractRequest, podId: e.target.value})}
                        placeholder="HU000130-11-S00000000000000012251"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Formátum: HU000000-00-S00000000000000000000</p>
                    </div>
                  </div>

                  {/* Szerződés időtartam */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Szerződés Időtartam
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Szerződés kezdete</label>
                        <input
                          type="date"
                          value={contractRequest.startDate}
                          onChange={(e) => setContractRequest({...contractRequest, startDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Szerződés vége</label>
                        <input
                          type="date"
                          value={contractRequest.endDate}
                          onChange={(e) => setContractRequest({...contractRequest, endDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tarifa és árazás */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Tarifa és Árazás
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tarifa típus</label>
                        <select
                          value={contractRequest.tariffType}
                          onChange={(e) => setContractRequest({...contractRequest, tariffType: e.target.value as any})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 bg-white"
                        >
                          <option value="residential">Lakossági</option>
                          <option value="commercial">Kereskedelmi</option>
                          <option value="industrial">Ipari</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Kapacitás (kW)</label>
                        <input
                          type="number"
                          value={contractRequest.capacity}
                          onChange={(e) => setContractRequest({...contractRequest, capacity: parseFloat(e.target.value) || 0})}
                          placeholder="10"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Árcsomag</label>
                      <input
                        type="text"
                        value={contractRequest.pricePackage}
                        onChange={(e) => setContractRequest({...contractRequest, pricePackage: e.target.value})}
                        placeholder="Alapcsomag, Zöld csomag, Üzleti csomag"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Havi alapdíj (HUF)</label>
                        <input
                          type="number"
                          value={contractRequest.monthlyFee}
                          onChange={(e) => setContractRequest({...contractRequest, monthlyFee: parseFloat(e.target.value) || 0})}
                          placeholder="2500"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Egységár (HUF/kWh)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={contractRequest.unitPrice}
                          onChange={(e) => setContractRequest({...contractRequest, unitPrice: parseFloat(e.target.value) || 0})}
                          placeholder="45.50"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Megjegyzések */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-sm font-medium text-gray-800 border-b border-gray-200 pb-2">
                      Megjegyzések
                    </h4>
                    
                    <div>
                      <textarea
                        value={contractRequest.notes}
                        onChange={(e) => setContractRequest({...contractRequest, notes: e.target.value})}
                        placeholder="Szerződéssel kapcsolatos megjegyzések, különleges feltételek..."
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Szerződések listája */}
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-800 flex items-center">
                        <ChartBarIcon className="w-5 h-5 mr-2 text-orange-600" />
                        Meglévő Szerződések
                      </h4>
                      <button
                        onClick={() => {
                          // Új szerződés hozzáadása a listához
                          const newContract = {
                            ...contractRequest,
                            id: `CONTRACT-${Date.now()}`,
                          };
                          setContracts([...contracts, newContract]);
                          
                          // Form reset
                          setContractRequest({
                            customerId: '',
                            customerName: '',
                            podId: '',
                            contractType: 'new',
                            contractNumber: '',
                            startDate: '',
                            endDate: '',
                            provider: 'EON',
                            tariffType: 'residential',
                            capacity: 0,
                            pricePackage: '',
                            monthlyFee: 0,
                            unitPrice: 0,
                            status: 'draft',
                            notes: ''
                          });
                        }}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                      >
                        <PlusIcon className="w-4 h-4 mr-1" />
                        Hozzáadás
                      </button>
                    </div>

                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {contracts.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <BuildingOffice2Icon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-sm">Még nincsenek szerződések</p>
                        </div>
                      ) : (
                        contracts.map((contract, index) => (
                          <div key={contract.id || index} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <div className={`w-3 h-3 rounded-full ${
                                  contract.status === 'active' ? 'bg-green-400' :
                                  contract.status === 'pending' ? 'bg-yellow-400' :
                                  contract.status === 'expired' ? 'bg-red-400' :
                                  contract.status === 'terminated' ? 'bg-gray-400' :
                                  'bg-blue-400'
                                }`}></div>
                                <span className="text-sm font-medium text-gray-900">
                                  {contract.contractNumber || 'Új szerződés'}
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  setContracts(contracts.filter((_, i) => i !== index));
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                <XCircleIcon className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="text-xs text-gray-600 space-y-1">
                              <div><strong>Ügyfél:</strong> {contract.customerName}</div>
                              <div><strong>POD:</strong> {contract.podId}</div>
                              <div><strong>Szolgáltató:</strong> {contract.provider}</div>
                              <div><strong>Típus:</strong> {contract.contractType}</div>
                              <div><strong>Időtartam:</strong> {contract.startDate} - {contract.endDate}</div>
                              <div><strong>Havi díj:</strong> {contract.monthlyFee} HUF</div>
                              <div><strong>Egységár:</strong> {contract.unitPrice} HUF/kWh</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Energia szerződések</span> - Teljes életciklus kezelés
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowContractManagement(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    Bezárás
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        // Itt lehetne Firebase-ba menteni a szerződéseket
                        // Egyelőre csak lokálisan tároljuk
                        alert(`${contracts.length} szerződés mentve!`);
                      } catch (error) {
                        console.error('Mentés hiba:', error);
                        alert('Hiba történt a mentés során!');
                      }
                    }}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    <ServerIcon className="w-4 h-4 mr-2 inline" />
                    Mentés
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnergiaUTILMD; 